/**
 * What the Saf3AI Agent knows about the page it is opened on.
 *
 * Pure functions over the AxonPageContext value, lifted out of the old chat
 * panel so the agent panel and anything else can share them. `pageId` is
 * `pageContext.page.id`; `summary` is whatever the page published through
 * `setPageContext({ summary })`.
 */
import { geminiTimeRangeForContext } from '../../../utils/geminiChatDateRange';

export const buildEnrichedContext = (pageContext, pageId) => {
  const ctx = { ...(pageContext || {}) };
  // Determine module from page ID
  const isEndpointPage = pageId?.startsWith('endpoint-security') || pageId?.startsWith('admin-endpoint') || pageId?.startsWith('admin-fleet')
    || pageId?.startsWith('admin-shadow-ai') || pageId?.startsWith('admin-dlp')
    || pageId?.startsWith('admin-ai-tool') || pageId?.startsWith('admin-ai-conversations') || pageId?.startsWith('admin-policy-audit');
  const module = pageId?.startsWith('gemini-agents-') ? 'gemini-enterprise'
    : pageId?.startsWith('gemini-') ? 'gemini-workspace'
    : pageId?.startsWith('copilot-') ? 'copilot'
    : pageId?.startsWith('saas-ai-') ? 'saas-ai'
    : pageId?.startsWith('finops') ? 'finops'
    : pageId?.startsWith('security-') ? 'security'
    : isEndpointPage ? 'endpoint-security'
    : pageId?.startsWith('admin-') ? 'admin'
    : 'general';
  ctx.module = module;
  if (ctx.module === 'gemini-enterprise') {
    // The GE pages' active date window — the backend pins answers to it.
    ctx.time_range = geminiTimeRangeForContext();
  }
  // Include page summary data if available
  if (ctx.summary && Object.keys(ctx.summary).length > 0) {
    ctx.page_data_summary = Object.entries(ctx.summary)
      .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
      .join(', ');
  }
  return ctx;
};

export const buildContextCards = (pageContext, pageId) => {
  const summary = pageContext?.summary;
  if (!summary || Object.keys(summary).length === 0) return [];
  const cards = [];
  const id = pageId;

  const fmtVal = (num) => {
    if (num === undefined || num === null) return '—';
    if (typeof num === 'string') return num;
    return Number(num).toLocaleString('en-US');
  };

  if (id === 'gemini-agents-dashboard') {
    if (summary.total_interactions !== undefined)
      cards.push({ label: 'Total Interactions', value: fmtVal(summary.total_interactions), icon: 'message-square', color: '#7B68EE', query: 'Break down total interactions by agent and trend' });
    if (summary.active_agents !== undefined)
      cards.push({ label: 'Active Agents', value: summary.active_agents, icon: 'cpu', color: '#46D9C5', query: 'List all active agents and their status' });
    if (summary.success_rate !== undefined)
      cards.push({ label: 'Success Rate', value: `${summary.success_rate}%`, icon: 'check-circle', color: summary.success_rate >= 90 ? '#22c55e' : summary.success_rate >= 75 ? '#eab308' : '#ef4444', query: `Why is the success rate ${summary.success_rate}%? Which agents are underperforming?` });
    // Threats: AI interactions graded high or critical (interactions.flagged).
    if (summary.threats_flagged !== undefined)
      cards.push({ label: 'Threats', value: summary.threats_flagged, icon: 'alert-triangle', color: summary.threats_flagged > 0 ? '#ef4444' : '#22c55e', query: `Analyze the ${summary.threats_flagged} threats (interactions graded high or critical)` });
    // Signals: detections and enforcements (security.flagged_signals), not threats.
    if (summary.signals_flagged !== undefined)
      cards.push({ label: 'Signals', value: summary.signals_flagged, icon: 'activity', color: summary.signals_flagged > 0 ? '#f97316' : '#22c55e', query: `Analyze the ${summary.signals_flagged} flagged signals (detections and enforcements)` });
    if (summary.avg_grounding_coverage !== undefined) {
      // avg_grounding_coverage is a decimal (e.g. 0.65 = 65%)
      const groundingPct = Math.round(summary.avg_grounding_coverage * 100);
      cards.push({ label: 'Grounding', value: `${groundingPct}%`, icon: 'link', color: groundingPct >= 80 ? '#22c55e' : '#eab308', query: 'Which agents have low grounding coverage and why?' });
    }
    if (summary.estimated_hours_saved !== undefined)
      cards.push({ label: 'Hours Saved', value: summary.estimated_hours_saved, icon: 'clock', color: '#3b82f6', query: 'How are the estimated hours saved calculated?' });
  }

  if (id === 'gemini-agents-registry') {
    if (summary.total_agents !== undefined)
      cards.push({ label: 'Discovered Agents', value: summary.total_agents, icon: 'server', color: '#7B68EE', query: `Give me an overview of all ${summary.total_agents} discovered agents` });
    if (summary.total_interactions !== undefined)
      cards.push({ label: 'Total Interactions', value: fmtVal(summary.total_interactions), icon: 'message-square', color: '#46D9C5', query: 'Which agents have the most interactions?' });
    if (summary.avg_success_rate !== undefined)
      cards.push({ label: 'Avg Success', value: `${summary.avg_success_rate}%`, icon: 'check-circle', color: parseFloat(summary.avg_success_rate) >= 90 ? '#22c55e' : '#eab308', query: `The average success rate is ${summary.avg_success_rate}%. Which agents are below average?` });
  }

  if (id === 'gemini-agents-usage-roi') {
    if (summary.total_interactions !== undefined)
      cards.push({ label: 'Total Interactions', value: fmtVal(summary.total_interactions), icon: 'message-square', color: '#7B68EE', query: 'Show interaction trends and breakdown by agent' });
    if (summary.active_agents !== undefined)
      cards.push({ label: 'Active Agents', value: summary.active_agents, icon: 'cpu', color: '#46D9C5', query: 'Which agents are most active and driving the most value?' });
    if (summary.complexity_adjusted_value !== undefined)
      cards.push({ label: 'ROI Value', value: `$${fmtVal(summary.complexity_adjusted_value)}`, icon: 'dollar-sign', color: '#22c55e', query: 'Explain the ROI calculation and breakdown by complexity tier' });
    if (summary.success_rate !== undefined)
      cards.push({ label: 'Success Rate', value: `${summary.success_rate}%`, icon: 'check-circle', color: summary.success_rate >= 90 ? '#22c55e' : '#eab308', query: 'What factors affect the success rate?' });
    if (summary.satisfaction_rate !== undefined)
      cards.push({ label: 'Satisfaction', value: `${summary.satisfaction_rate}%`, icon: 'heart', color: summary.satisfaction_rate >= 80 ? '#ec4899' : '#eab308', query: `User satisfaction is ${summary.satisfaction_rate}%. What drives thumbs up vs down?` });
    if (summary.estimated_hours_saved !== undefined)
      cards.push({ label: 'Hours Saved', value: summary.estimated_hours_saved, icon: 'clock', color: '#3b82f6', query: 'How are hours saved estimated and which agents save the most time?' });
  }

  if (id === 'gemini-agents-threats') {
    if (summary.total_threats !== undefined)
      cards.push({ label: 'Detections', value: summary.total_threats, icon: 'alert-triangle', color: summary.total_threats > 0 ? '#ef4444' : '#22c55e', query: `Analyze all ${summary.total_threats} detections — breakdown by type and severity` });
    if (summary.by_severity?.critical !== undefined)
      cards.push({ label: 'Critical', value: summary.by_severity.critical, icon: 'alert-octagon', color: '#ef4444', query: 'Show all critical severity threats with details' });
    if (summary.by_severity?.high !== undefined)
      cards.push({ label: 'High', value: summary.by_severity.high, icon: 'shield', color: '#f97316', query: 'Show all high severity threats with details' });
    if (summary.by_severity?.medium !== undefined)
      cards.push({ label: 'Medium', value: summary.by_severity.medium, icon: 'info', color: '#eab308', query: 'Show medium severity threats' });
    if (summary.by_category && typeof summary.by_category === 'object')
      cards.push({ label: 'Categories', value: Object.keys(summary.by_category).length, icon: 'layers', color: '#7B68EE', query: 'What threat categories are detected and which are most common?' });
  }

  if (id === 'gemini-agents-conversations') {
    if (summary.total_sessions !== undefined)
      cards.push({ label: 'Total Sessions', value: fmtVal(summary.total_sessions), icon: 'message-square', color: '#7B68EE', query: `Analyze the ${summary.total_sessions} sessions — any patterns or anomalies?` });
    if (summary.unique_agents !== undefined)
      cards.push({ label: 'Unique Agents', value: summary.unique_agents, icon: 'cpu', color: '#46D9C5', query: 'Which agents are most active in conversations?' });
    if (summary.avg_success_rate !== undefined)
      cards.push({ label: 'Avg Success', value: `${summary.avg_success_rate}%`, icon: 'check-circle', color: parseFloat(summary.avg_success_rate) >= 90 ? '#22c55e' : '#eab308', query: 'Which conversations had failures and why?' });
    if (summary.threats_in_sessions > 0)
      cards.push({ label: 'Detections', value: summary.threats_in_sessions, icon: 'alert-triangle', color: '#ef4444', query: `${summary.threats_in_sessions} detections in conversations — show details` });
  }

  // Endpoint Security pages
  if (id === 'admin-endpoint-violations') {
    if (summary.total !== undefined)
      cards.push({ label: 'Total Events', value: fmtVal(summary.total), icon: 'activity', color: '#7B68EE', query: 'Break down all endpoint events by type and severity' });
    if (summary.dlp !== undefined)
      cards.push({ label: 'DLP Violations', value: fmtVal(summary.dlp), icon: 'shield', color: summary.dlp > 0 ? '#f97316' : '#22c55e', query: 'Analyze DLP violations — top categories and users' });
    if (summary.shadowAi !== undefined)
      cards.push({ label: 'Shadow AI', value: fmtVal(summary.shadowAi), icon: 'eye', color: summary.shadowAi > 0 ? '#f97316' : '#22c55e', query: 'Which AI tools are being used most and which are blocked?' });
    if (summary.critical !== undefined)
      cards.push({ label: 'Critical/High', value: fmtVal(summary.critical), icon: 'alert-triangle', color: summary.critical > 0 ? '#ef4444' : '#22c55e', query: 'Show all critical and high severity events with details' });
  }

  if (id === 'admin-shadow-ai-discovery') {
    if (summary.totalDetections !== undefined)
      cards.push({ label: 'Total Detections', value: fmtVal(summary.totalDetections), icon: 'eye', color: '#7B68EE', query: 'Break down Shadow AI detections by tool and category' });
    if (summary.directCount !== undefined)
      cards.push({ label: 'Direct Access', value: fmtVal(summary.directCount), icon: 'message-square', color: '#f97316', query: 'Which direct AI tools are users visiting most?' });
    if (summary.embeddedCount !== undefined)
      cards.push({ label: 'Embedded AI', value: fmtVal(summary.embeddedCount), icon: 'code', color: '#7B68EE', query: 'What embedded AI widgets are detected?' });
    if (summary.apiCount !== undefined)
      cards.push({ label: 'API Calls', value: fmtVal(summary.apiCount), icon: 'server', color: '#06b6d4', query: 'Which AI APIs are being called from browser?' });
    if (summary.uniqueTools !== undefined)
      cards.push({ label: 'Unique Tools', value: fmtVal(summary.uniqueTools), icon: 'grid', color: '#46D9C5', query: 'List all unique AI tools detected' });
  }

  if (id === 'admin-fleet-overview') {
    if (summary.activeEndpoints !== undefined)
      cards.push({ label: 'Active Endpoints', value: fmtVal(summary.activeEndpoints), icon: 'monitor', color: '#22c55e', query: 'Show details of all active endpoints' });
    if (summary.totalEndpoints !== undefined)
      cards.push({ label: 'Total Endpoints', value: fmtVal(summary.totalEndpoints), icon: 'server', color: '#7B68EE', query: 'Show all registered endpoints with their status' });
    if (summary.policyCompliance !== undefined)
      cards.push({ label: 'Policy Compliance', value: `${summary.policyCompliance}%`, icon: 'check-circle', color: summary.policyCompliance >= 90 ? '#22c55e' : '#f97316', query: 'Which endpoints are not on the latest policy?' });
  }

  if (id === 'admin-dlp-analytics') {
    if (summary.totalBlocked !== undefined)
      cards.push({ label: 'Blocked', value: fmtVal(summary.totalBlocked), icon: 'slash', color: '#ef4444', query: 'Show all blocked DLP events with details' });
    if (summary.totalWarned !== undefined)
      cards.push({ label: 'Warned', value: fmtVal(summary.totalWarned), icon: 'alert-triangle', color: '#f97316', query: 'Show warned DLP events — should any be upgraded to block?' });
    if (summary.topCategory !== undefined)
      cards.push({ label: 'Top Category', value: summary.topCategory, icon: 'layers', color: '#7B68EE', query: `Why is ${summary.topCategory} the most common DLP category?` });
  }

  if (id === 'admin-endpoint-user-activity') {
    if (summary.uniqueUsers !== undefined)
      cards.push({ label: 'Unique Users', value: fmtVal(summary.uniqueUsers), icon: 'users', color: '#7B68EE', query: 'Show user breakdown by activity type' });
    if (summary.usersWithBlocks !== undefined)
      cards.push({ label: 'Users with Blocks', value: fmtVal(summary.usersWithBlocks), icon: 'slash', color: summary.usersWithBlocks > 0 ? '#ef4444' : '#22c55e', query: 'Which users had the most blocked events?' });
    if (summary.topUser !== undefined)
      cards.push({ label: 'Top User', value: summary.topUser, icon: 'user', color: '#f97316', query: `Why does ${summary.topUser} have the most violations?` });
  }

  if (id === 'admin-ai-tool-catalog') {
    if (summary.totalTools !== undefined)
      cards.push({ label: 'Total Tools', value: fmtVal(summary.totalTools), icon: 'grid', color: '#7B68EE', query: 'Give me an overview of all AI tools in the catalog' });
    if (summary.configuredCount !== undefined)
      cards.push({ label: 'Configured', value: fmtVal(summary.configuredCount), icon: 'check-circle', color: '#22c55e', query: 'Which tools are configured and what actions are set?' });
    if (summary.blockedCount !== undefined)
      cards.push({ label: 'Blocked', value: fmtVal(summary.blockedCount), icon: 'slash', color: '#ef4444', query: 'Which AI tools are currently blocked?' });
    if (summary.monitoredCount !== undefined)
      cards.push({ label: 'Monitored', value: fmtVal(summary.monitoredCount), icon: 'eye', color: '#f97316', query: 'Which AI tools are being monitored?' });
  }

  if (id === 'admin-ai-conversations') {
    if (summary.totalConversations !== undefined)
      cards.push({ label: 'Conversations', value: fmtVal(summary.totalConversations), icon: 'message-square', color: '#7B68EE', query: 'Break down conversations by tool and user' });
    if (summary.uniqueUsers !== undefined)
      cards.push({ label: 'Unique Users', value: fmtVal(summary.uniqueUsers), icon: 'users', color: '#06b6d4', query: 'Which users have the most AI conversations?' });
    if (summary.totalPrompts !== undefined)
      cards.push({ label: 'Total Prompts', value: fmtVal(summary.totalPrompts), icon: 'edit-3', color: '#f97316', query: 'Show prompt volume trends' });
    if (summary.topTool !== undefined)
      cards.push({ label: 'Top Tool', value: summary.topTool, icon: 'trending-up', color: '#22c55e', query: `Why is ${summary.topTool} the most used AI tool?` });
  }

  return cards.slice(0, 6);
};

export const getDataDrivenActions = (pageContext, pageId) => {
  const summary = pageContext?.summary;
  if (!summary || Object.keys(summary).length === 0) return [];
  const actions = [];
  const id = pageId;

  // Dashboard-specific insights
  if (id === 'gemini-agents-dashboard') {
    if (summary.success_rate !== undefined && summary.success_rate < 80)
      actions.push({ label: `Why is success rate ${summary.success_rate}%?`, icon: 'alert-circle' });
    if (summary.threats_flagged > 0)
      actions.push({ label: `Analyze ${summary.threats_flagged} threats`, icon: 'shield' });
    if (summary.signals_flagged > 0)
      actions.push({ label: `Analyze ${summary.signals_flagged} flagged signals`, icon: 'activity' });
    if (summary.top_agents)
      actions.push({ label: 'Compare top agent performance', icon: 'bar-chart-2' });
  }

  // Registry-specific insights
  if (id === 'gemini-agents-registry') {
    if (summary.total_agents)
      actions.push({ label: `Overview of ${summary.total_agents} discovered agents`, icon: 'server' });
    if (summary.avg_success_rate && parseFloat(summary.avg_success_rate) < 90)
      actions.push({ label: `Why is avg success rate ${summary.avg_success_rate}%?`, icon: 'alert-circle' });
  }

  // Usage & ROI-specific insights
  if (id === 'gemini-agents-usage-roi') {
    if (summary.complexity_adjusted_value)
      actions.push({ label: 'Explain ROI breakdown', icon: 'dollar-sign' });
    if (summary.satisfaction_rate !== undefined)
      actions.push({ label: `User satisfaction is ${summary.satisfaction_rate}% — insights`, icon: 'heart' });
    if (summary.top_agents)
      actions.push({ label: 'Which agents drive the most value?', icon: 'trending-up' });
  }

  // Threats-specific insights
  if (id === 'gemini-agents-threats') {
    if (summary.total_threats > 0) {
      actions.push({ label: `Analyze ${summary.total_threats} threats`, icon: 'alert-triangle' });
      if (summary.by_severity?.critical > 0 || summary.by_severity?.high > 0)
        actions.push({ label: `${summary.by_severity?.critical || 0} critical, ${summary.by_severity?.high || 0} high severity — explain risks`, icon: 'shield' });
    }
  }

  // Conversations-specific insights
  if (id === 'gemini-agents-conversations') {
    if (summary.total_sessions)
      actions.push({ label: `Analyze ${summary.total_sessions} sessions`, icon: 'message-square' });
    if (summary.threats_in_sessions > 0)
      actions.push({ label: `${summary.threats_in_sessions} threats in conversations — details`, icon: 'alert-triangle' });
  }

  return actions;
};

// Page-specific quick actions

export const getQuickActionsForPage = (currentPageId) => {
  switch (currentPageId) {
    // Monitoring Section
    case 'agent-network':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Network' },
        { label: 'Agent Overview', icon: 'box' },
        { label: 'Network Connections', icon: 'git-branch' },
        { label: 'Agent Health', icon: 'activity' },
        { label: 'Top Agents', icon: 'trending-up' },
      ];
    case 'conversation-explorer':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Conversations' },
        { label: 'High-Threat Conversations', icon: 'alert-triangle' },
        { label: 'Threat Analysis', icon: 'shield' },
        { label: 'Agent Activity', icon: 'activity' },
        { label: 'Conversation Trends', icon: 'trending-up' },
      ];
    case 'user-behavior':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Users' },
        { label: 'User Threat Analysis', icon: 'alert-circle' },
        { label: 'Activity Patterns', icon: 'activity' },
        { label: 'Top Users', icon: 'users' },
        { label: 'Anomalous Behavior', icon: 'alert-triangle' },
      ];
    case 'observability':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Dashboard' },
        { label: 'System Health', icon: 'activity' },
        { label: 'Performance Metrics', icon: 'trending-up' },
        { label: 'Error Analysis', icon: 'alert-circle' },
        { label: 'Resource Usage', icon: 'bar-chart-2' },
      ];
    case 'global-search':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Hunt' },
        { label: 'Search Tips', icon: 'search' },
        { label: 'Recent Searches', icon: 'clock' },
        { label: 'Common Queries', icon: 'help-circle' },
      ];

    // Security and Compliance Section
    case 'security-threat-control':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Threats Control' },
        { label: 'Active Threats', icon: 'alert-triangle' },
        { label: 'Threat Trends', icon: 'trending-up' },
        { label: 'Mitigation Status', icon: 'shield' },
        { label: 'Recent Incidents', icon: 'clock' },
      ];
    case 'security-data-security':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Access Management' },
        { label: 'Data Exposure Risks', icon: 'lock' },
        { label: 'Compliance Status', icon: 'check-circle' },
        { label: 'Data Access Patterns', icon: 'activity' },
        { label: 'Security Violations', icon: 'alert-triangle' },
      ];
    case 'security-threat-intelligence':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Source Intelligence' },
        { label: 'Threat Indicators', icon: 'target' },
        { label: 'IOC Analysis', icon: 'search' },
        { label: 'Threat Feeds', icon: 'rss' },
        { label: 'Threat Assessment', icon: 'bar-chart-2' },
      ];
    case 'security-attack-path':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Attack Path' },
        { label: 'Attack Vectors', icon: 'git-branch' },
        { label: 'Path Analysis', icon: 'map' },
        { label: 'Critical Paths', icon: 'alert-triangle' },
        { label: 'Vulnerability Chain', icon: 'link' },
      ];
    case 'security-audit-log':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Audit Log' },
        { label: 'Recent Events', icon: 'clock' },
        { label: 'Access Logs', icon: 'file-text' },
        { label: 'Compliance Audit', icon: 'check-circle' },
        { label: 'Suspicious Activity', icon: 'alert-triangle' },
      ];
    case 'reports':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Security Insights' },
        { label: 'Security Overview', icon: 'shield' },
        { label: 'Threat Summary', icon: 'alert-triangle' },
        { label: 'Compliance Report', icon: 'bar-chart-2' },
        { label: 'Threat Assessment', icon: 'trending-up' },
      ];
    case 'admin-compliance':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Compliance Center' },
        { label: 'Compliance Status', icon: 'clipboard' },
        { label: 'Control Coverage', icon: 'check-circle' },
        { label: 'Gap Analysis', icon: 'alert-circle' },
        { label: 'Remediation Plan', icon: 'target' },
      ];
    case 'admin-custom-guardrails':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Custom Guardrails' },
        { label: 'Active Rules', icon: 'shield' },
        { label: 'Rule Effectiveness', icon: 'bar-chart-2' },
        { label: 'Violation Trends', icon: 'trending-up' },
        { label: 'Rule Recommendations', icon: 'sunrise' },
      ];

    // FinOps Section
    case 'finops':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of LLM Cost Management' },
        { label: 'Cost Overview', icon: 'dollar-sign' },
        { label: 'Spending Trends', icon: 'trending-up' },
        { label: 'Top Cost Drivers', icon: 'bar-chart-2' },
        { label: 'Optimization Opportunities', icon: 'target' },
      ];
    case 'finops-simulator':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of LLM Cost Simulator' },
        { label: 'Cost Projections', icon: 'trending-up' },
        { label: 'Model Comparison', icon: 'sliders' },
        { label: 'Usage Scenarios', icon: 'layers' },
        { label: 'Savings Potential', icon: 'dollar-sign' },
      ];
    case 'finops-roi':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of ROI Analysis' },
        { label: 'ROI Metrics', icon: 'dollar-sign' },
        { label: 'Value Drivers', icon: 'trending-up' },
        { label: 'Cost-Benefit Analysis', icon: 'bar-chart-2' },
        { label: 'Investment Impact', icon: 'target' },
      ];
    case 'finops-budget':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Budget & Forecasting' },
        { label: 'Budget Status', icon: 'bar-chart' },
        { label: 'Forecast Accuracy', icon: 'trending-up' },
        { label: 'Budget Alerts', icon: 'alert-circle' },
        { label: 'Spending Trends', icon: 'activity' },
      ];
    case 'finops-allocation':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Cost Allocation' },
        { label: 'Cost by Team', icon: 'users' },
        { label: 'Cost by Project', icon: 'briefcase' },
        { label: 'Allocation Rules', icon: 'settings' },
        { label: 'Chargeback Report', icon: 'file-text' },
      ];
    case 'finops-greenops':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of GreenOps' },
        { label: 'Carbon Footprint', icon: 'globe' },
        { label: 'Energy Consumption', icon: 'zap' },
        { label: 'Sustainability Metrics', icon: 'trending-up' },
        { label: 'Green Initiatives', icon: 'leaf' },
      ];
    case 'finops-settings':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of FinOps Settings' },
        { label: 'Configuration Overview', icon: 'settings' },
        { label: 'Cost Tracking Setup', icon: 'dollar-sign' },
        { label: 'Alert Configuration', icon: 'bell' },
      ];

    // Evaluations Section
    case 'model-evaluation':
    case 'model-evaluation-projects':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Model Evaluation' },
        { label: 'Evaluation Results', icon: 'bar-chart-2' },
        { label: 'Model Performance', icon: 'trending-up' },
        { label: 'Comparison Analysis', icon: 'layers' },
        { label: 'Best Practices', icon: 'book-open' },
      ];
    case 'red-team-scanner':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Red Team Audit' },
        { label: 'Security Findings', icon: 'shield' },
        { label: 'Vulnerability Report', icon: 'alert-triangle' },
        { label: 'Threat Assessment', icon: 'bar-chart-2' },
        { label: 'Remediation Steps', icon: 'target' },
      ];

    // Governance & Control Section
    case 'dashboard':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Events Log' },
        { label: 'Security Overview', icon: 'shield' },
        { label: 'Threat Summary', icon: 'alert-triangle' },
        { label: 'Agent Performance', icon: 'bar-chart-2' },
        { label: 'Recent Events', icon: 'clock' },
      ];
    case 'admin-policies':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Policy Manager' },
        { label: 'Active Policies', icon: 'shield' },
        { label: 'Policy Compliance', icon: 'check-circle' },
        { label: 'Violation Trends', icon: 'alert-triangle' },
        { label: 'Policy Recommendations', icon: 'sunrise' },
      ];
    case 'admin-endpoint-settings':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Endpoint Settings' },
        { label: 'Configuration Overview', icon: 'settings' },
        { label: 'Endpoint Status', icon: 'monitor' },
        { label: 'Settings Best Practices', icon: 'book-open' },
      ];
    case 'admin-endpoint-violations':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Endpoint Violations' },
        { label: 'Recent Violations', icon: 'alert-triangle' },
        { label: 'Violation Trends', icon: 'trending-up' },
        { label: 'Critical Issues', icon: 'shield-off' },
        { label: 'Remediation Status', icon: 'check-circle' },
      ];
    case 'admin-fleet-overview':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Fleet Overview' },
        { label: 'Active Endpoints', icon: 'monitor' },
        { label: 'Policy Compliance', icon: 'check-circle' },
        { label: 'OS & Browser Distribution', icon: 'pie-chart' },
        { label: 'Extension Health', icon: 'activity' },
      ];
    case 'admin-shadow-ai-discovery':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Shadow AI Discovery' },
        { label: 'Top AI Tools Detected', icon: 'trending-up' },
        { label: 'Detection Trends', icon: 'bar-chart-2' },
        { label: 'Threat AI Tools', icon: 'alert-triangle' },
        { label: 'User AI Adoption', icon: 'users' },
      ];
    case 'admin-dlp-analytics':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of DLP Analytics' },
        { label: 'DLP Violation Trends', icon: 'trending-up' },
        { label: 'Top Blocked Categories', icon: 'shield' },
        { label: 'High-Threat Patterns', icon: 'alert-triangle' },
        { label: 'Category Breakdown', icon: 'pie-chart' },
      ];
    case 'admin-endpoint-user-activity':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Endpoint User Activity' },
        { label: 'Top Users by Violations', icon: 'users' },
        { label: 'User Threat Analysis', icon: 'alert-circle' },
        { label: 'Activity Trends', icon: 'trending-up' },
        { label: 'Device Summary', icon: 'monitor' },
      ];
    case 'admin-ai-tool-catalog':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of AI Tool Catalog' },
        { label: 'Tool Coverage Overview', icon: 'grid' },
        { label: 'Blocked Tools', icon: 'slash' },
        { label: 'Unconfigured Tools', icon: 'help-circle' },
        { label: 'Threat Assessment', icon: 'shield' },
      ];
    case 'admin-ai-conversations':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of AI Conversations' },
        { label: 'Top Talkers', icon: 'users' },
        { label: 'Sensitive Conversations', icon: 'alert-triangle' },
        { label: 'Tool Breakdown', icon: 'pie-chart' },
        { label: 'Recent Activity', icon: 'clock' },
      ];
    case 'admin-policy-audit-log':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Policy Audit Log' },
        { label: 'Recent Policy Changes', icon: 'clock' },
        { label: 'Who Changed What', icon: 'user' },
        { label: 'Policy Version History', icon: 'git-commit' },
      ];
    case 'alerts':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Alerts Dashboard' },
        { label: 'Critical Alerts', icon: 'alert-circle' },
        { label: 'Alert Trends', icon: 'trending-up' },
        { label: 'Most Frequent Alerts', icon: 'bar-chart-2' },
        { label: 'Unread Alerts', icon: 'bell' },
      ];

    // Enterprise AI - Copilot Section
    case 'copilot-dashboard':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Activation Dashboard' },
        { label: 'Usage Overview', icon: 'cpu' },
        { label: 'Threat Summary', icon: 'alert-triangle' },
        { label: 'User Activity', icon: 'users' },
        { label: 'Performance Metrics', icon: 'trending-up' },
      ];
    case 'copilot-interactions':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Conversations' },
        { label: 'Recent Conversations', icon: 'message-square' },
        { label: 'Conversation Patterns', icon: 'activity' },
        { label: 'Top Users', icon: 'users' },
        { label: 'Usage Trends', icon: 'trending-up' },
      ];
    case 'copilot-threats':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Alerts' },
        { label: 'Active Alerts', icon: 'alert-triangle' },
        { label: 'Alert Trends', icon: 'trending-up' },
        { label: 'Threat Assessment', icon: 'shield' },
        { label: 'Mitigation Status', icon: 'check-circle' },
      ];
    case 'copilot-users':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Users' },
        { label: 'User Activity', icon: 'users' },
        { label: 'Usage Patterns', icon: 'activity' },
        { label: 'Top Users', icon: 'trending-up' },
        { label: 'Threat Users', icon: 'alert-triangle' },
      ];
    case 'copilot-applications':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Copilot Applications' },
        { label: 'Application Usage', icon: 'grid' },
        { label: 'Top Applications', icon: 'trending-up' },
        { label: 'Usage Trends', icon: 'bar-chart-2' },
        { label: 'Security Status', icon: 'shield' },
      ];
    case 'copilot-sessions':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Copilot Sessions' },
        { label: 'Active Sessions', icon: 'git-branch' },
        { label: 'Session Patterns', icon: 'activity' },
        { label: 'Session Duration', icon: 'clock' },
        { label: 'Anomalous Sessions', icon: 'alert-triangle' },
      ];
    case 'copilot-intent-analysis':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Intent Analysis' },
        { label: 'Intent Categories', icon: 'target' },
        { label: 'Intent Trends', icon: 'trending-up' },
        { label: 'Common Intents', icon: 'bar-chart-2' },
        { label: 'Threat Intents', icon: 'alert-triangle' },
      ];
    case 'copilot-knowledge-gaps':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Knowledge Gaps' },
        { label: 'Identified Gaps', icon: 'book-open' },
        { label: 'Gap Trends', icon: 'trending-up' },
        { label: 'Critical Gaps', icon: 'alert-triangle' },
        { label: 'Remediation Plan', icon: 'target' },
      ];
    case 'copilot-usage-roi':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Usage & ROI' },
        { label: 'ROI Metrics', icon: 'dollar-sign' },
        { label: 'Usage Trends', icon: 'trending-up' },
        { label: 'Value Drivers', icon: 'bar-chart-2' },
        { label: 'Cost Analysis', icon: 'activity' },
      ];
    case 'copilot-audit-log':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Audit Log' },
        { label: 'Recent Events', icon: 'clock' },
        { label: 'Access Logs', icon: 'file-text' },
        { label: 'Compliance Audit', icon: 'check-circle' },
        { label: 'Suspicious Activity', icon: 'alert-triangle' },
      ];
    case 'copilot-compliance':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Compliance' },
        { label: 'Compliance Status', icon: 'clipboard' },
        { label: 'Control Coverage', icon: 'check-circle' },
        { label: 'Gap Analysis', icon: 'alert-circle' },
        { label: 'Remediation Plan', icon: 'target' },
      ];
    case 'copilot-settings':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Microsoft 365 Copilot Settings' },
        { label: 'Configuration Overview', icon: 'settings' },
        { label: 'Security Settings', icon: 'shield' },
        { label: 'Best Practices', icon: 'book-open' },
      ];

    // Enterprise AI - Gemini Section
    case 'gemini-dashboard':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Dashboard' },
        { label: 'Usage Overview', icon: 'cpu' },
        { label: 'Threat Summary', icon: 'alert-triangle' },
        { label: 'User Activity', icon: 'users' },
        { label: 'Performance Metrics', icon: 'trending-up' },
      ];
    case 'gemini-interactions':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Interactions' },
        { label: 'Recent Interactions', icon: 'message-square' },
        { label: 'Interaction Patterns', icon: 'activity' },
        { label: 'Top Users', icon: 'users' },
        { label: 'Usage Trends', icon: 'trending-up' },
      ];
    case 'gemini-threats':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Threats' },
        { label: 'Active Threats', icon: 'alert-triangle' },
        { label: 'Threat Trends', icon: 'trending-up' },
        { label: 'Threat Assessment', icon: 'shield' },
        { label: 'Mitigation Status', icon: 'check-circle' },
      ];
    case 'gemini-users':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Users' },
        { label: 'User Activity', icon: 'users' },
        { label: 'Usage Patterns', icon: 'activity' },
        { label: 'Top Users', icon: 'trending-up' },
        { label: 'Threat Users', icon: 'alert-triangle' },
      ];
    case 'gemini-applications':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Applications' },
        { label: 'Application Usage', icon: 'grid' },
        { label: 'Top Applications', icon: 'trending-up' },
        { label: 'Usage Trends', icon: 'bar-chart-2' },
        { label: 'Security Status', icon: 'shield' },
      ];
    case 'gemini-intent-analysis':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Intent Analysis' },
        { label: 'Intent Categories', icon: 'target' },
        { label: 'Intent Trends', icon: 'trending-up' },
        { label: 'Common Intents', icon: 'bar-chart-2' },
        { label: 'Threat Intents', icon: 'alert-triangle' },
      ];
    case 'gemini-usage-roi':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Usage & ROI' },
        { label: 'ROI Metrics', icon: 'dollar-sign' },
        { label: 'Usage Trends', icon: 'trending-up' },
        { label: 'Value Drivers', icon: 'bar-chart-2' },
        { label: 'Cost Analysis', icon: 'activity' },
      ];
    case 'gemini-audit-log':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Audit Log' },
        { label: 'Recent Events', icon: 'clock' },
        { label: 'Access Logs', icon: 'file-text' },
        { label: 'Compliance Audit', icon: 'check-circle' },
        { label: 'Suspicious Activity', icon: 'alert-triangle' },
      ];
    case 'gemini-compliance':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Compliance' },
        { label: 'Compliance Status', icon: 'clipboard' },
        { label: 'Control Coverage', icon: 'check-circle' },
        { label: 'Gap Analysis', icon: 'alert-circle' },
        { label: 'Remediation Plan', icon: 'target' },
      ];
    case 'gemini-settings':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Settings' },
        { label: 'Configuration Overview', icon: 'settings' },
        { label: 'Security Settings', icon: 'shield' },
        { label: 'Best Practices', icon: 'book-open' },
      ];

    // Enterprise AI - Gemini Agents Section
    case 'gemini-agents-dashboard':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Gemini Agents Dashboard' },
        { label: 'Agent Usage Breakdown', icon: 'bar-chart-2' },
        { label: 'ROI & Productivity', icon: 'trending-up' },
        { label: 'Knowledge Gap Insights', icon: 'book-open' },
        { label: 'Compliance Overview', icon: 'clipboard' },
      ];
    case 'gemini-agents-conversations':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Entity Conversations' },
        { label: 'Session Quality Analysis', icon: 'message-square' },
        { label: 'Flagged Sessions', icon: 'alert-triangle' },
        { label: 'Grounding Coverage', icon: 'link' },
        { label: 'Intent Breakdown', icon: 'target' },
      ];
    case 'gemini-agents-threats':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Threat Analysis' },
        { label: 'Threat Breakdown', icon: 'alert-triangle' },
        { label: 'Severity Distribution', icon: 'bar-chart-2' },
        { label: 'Affected Agents', icon: 'cpu' },
        { label: 'Compliance Impact', icon: 'clipboard' },
      ];
    case 'gemini-agents-intent-analysis':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Intent Analysis' },
        { label: 'Intent Categories', icon: 'target' },
        { label: 'Intent Trends', icon: 'trending-up' },
        { label: 'Usage Patterns', icon: 'activity' },
        { label: 'Threat Intents', icon: 'alert-triangle' },
      ];
    case 'gemini-agents-sources':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Source Intelligence' },
        { label: 'Source Coverage', icon: 'book-open' },
        { label: 'Grounding Quality', icon: 'check-circle' },
        { label: 'Knowledge Gaps', icon: 'alert-circle' },
        { label: 'External Sources', icon: 'globe' },
      ];
    case 'gemini-agents-registry':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Registry' },
        { label: 'Agent Performance', icon: 'activity' },
        { label: 'Usage by Agent', icon: 'bar-chart-2' },
        { label: 'Grounding Analysis', icon: 'link' },
        { label: 'Agent Compliance', icon: 'clipboard' },
      ];
    case 'gemini-agents-audit-log':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Audit Log' },
        { label: 'Recent Events', icon: 'clock' },
        { label: 'Compliance Audit', icon: 'check-circle' },
        { label: 'User Access Logs', icon: 'users' },
        { label: 'Suspicious Activity', icon: 'alert-triangle' },
      ];
    case 'gemini-agents-knowledge-gaps':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Knowledge Gaps' },
        { label: 'Unresolved Queries', icon: 'help-circle' },
        { label: 'Gap Trends', icon: 'trending-up' },
        { label: 'Content Recommendations', icon: 'book-open' },
        { label: 'Agent-Specific Gaps', icon: 'cpu' },
      ];
    case 'gemini-agents-usage-roi':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Usage & ROI' },
        { label: 'Productivity Value', icon: 'trending-up' },
        { label: 'Usage by Agent', icon: 'bar-chart-2' },
        { label: 'Hours Saved', icon: 'clock' },
        { label: 'Adoption Trends', icon: 'users' },
      ];
    case 'gemini-agents-users':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Users' },
        { label: 'User Activity', icon: 'users' },
        { label: 'Adoption Metrics', icon: 'trending-up' },
        { label: 'Top Users', icon: 'award' },
        { label: 'User Threat Analysis', icon: 'alert-triangle' },
      ];
    case 'gemini-agents-compliance':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Compliance' },
        { label: 'Compliance Score', icon: 'clipboard' },
        { label: 'Control Coverage', icon: 'check-circle' },
        { label: 'Gap Analysis', icon: 'alert-circle' },
        { label: 'Remediation Plan', icon: 'target' },
      ];
    case 'gemini-agents-network':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Network' },
        { label: 'Agent Topology', icon: 'git-branch' },
        { label: 'Inter-Agent Activity', icon: 'activity' },
        { label: 'Top Agents', icon: 'cpu' },
        { label: 'Network Health', icon: 'heart' },
      ];
    case 'gemini-agents-grounding':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Grounding Analytics' },
        { label: 'Grounding Coverage', icon: 'link' },
        { label: 'Source Quality', icon: 'check-circle' },
        { label: 'Ungrounded Responses', icon: 'alert-triangle' },
        { label: 'Knowledge Gap Impact', icon: 'book-open' },
      ];
    case 'gemini-agents-posture':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Security Posture' },
        { label: 'IAM Analysis', icon: 'key' },
        { label: 'Configuration Drift', icon: 'git-branch' },
        { label: 'Instruction Threat', icon: 'alert-triangle' },
        { label: 'Data Store Security', icon: 'database' },
      ];
    case 'gemini-agents-settings':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agent Settings' },
        { label: 'Configuration Overview', icon: 'settings' },
        { label: 'Security Settings', icon: 'shield' },
        { label: 'Best Practices', icon: 'book-open' },
      ];

    // Management Section
    case 'admin-users':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Users' },
        { label: 'User Overview', icon: 'users' },
        { label: 'Role Distribution', icon: 'user-check' },
        { label: 'Active Users', icon: 'activity' },
        { label: 'User Activity', icon: 'clock' },
      ];
    case 'admin-agents':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Agents' },
        { label: 'Agent Overview', icon: 'user-check' },
        { label: 'Agent Status', icon: 'activity' },
        { label: 'Top Agents', icon: 'trending-up' },
        { label: 'Agent Health', icon: 'heart' },
      ];
    case 'admin-teams':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Teams' },
        { label: 'Team Overview', icon: 'briefcase' },
        { label: 'Team Structure', icon: 'users' },
        { label: 'Team Performance', icon: 'bar-chart-2' },
        { label: 'Team Activity', icon: 'activity' },
      ];
    // Alerts are configured by asking: each of these is a question the agent answers with the
    // alert tools, and any change comes back as an approval card the person can edit.
    case 'admin-alert-configuration':
      return [
        { label: 'Set up alerts for my team', icon: 'users' },
        { label: "Why didn't I get an alert?", icon: 'help-circle' },
        { label: 'Too many emails', icon: 'mail' },
        { label: 'What alerts are on?', icon: 'bell' },
      ];
    case 'admin-sso':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of SSO Settings' },
        { label: 'SSO Configuration', icon: 'key' },
        { label: 'Provider Status', icon: 'check-circle' },
        { label: 'User Access', icon: 'users' },
        { label: 'Security Settings', icon: 'shield' },
      ];

    // Custom Dashboards Section
    case 'ai-explorer':
    case 'ai-explorer-dashboards':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of AI Explorer' },
        { label: 'Recent Queries', icon: 'search' },
        { label: 'Saved Dashboards', icon: 'layout' },
        { label: 'Query History', icon: 'clock' },
        { label: 'Popular Queries', icon: 'trending-up' },
      ];

    // Account Hub Section
    case 'billing':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Billing & Usage' },
        { label: 'Usage Overview', icon: 'credit-card' },
        { label: 'Billing History', icon: 'file-text' },
        { label: 'Cost Breakdown', icon: 'dollar-sign' },
        { label: 'Payment Methods', icon: 'credit-card' },
      ];

    // Profile & Settings
    case 'profile':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Profile' },
        { label: 'Account Settings', icon: 'user' },
        { label: 'Preferences', icon: 'settings' },
      ];
    case 'change-password':
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of Change Password' },
        { label: 'Password Requirements', icon: 'lock' },
        { label: 'Security Tips', icon: 'shield' },
      ];

    default:
      // Default actions for unknown pages
      return [
        { label: 'Page Summary', icon: 'file-text', description: 'Get a summary of this page' },
        { label: 'Recent Activity', icon: 'clock' },
        { label: 'Overview', icon: 'bar-chart-2' },
        { label: 'Key Metrics', icon: 'trending-up' },
      ];
  }
};

/** Helper-mode prompts: conceptual questions, the same on every page. */
export const HELPER_QUICK_ACTIONS = [
  { label: 'What is an AI agent in this system?', icon: 'cpu' },
  { label: 'What key metrics define AI system health?', icon: 'activity' },
  { label: 'How can overall AI security posture be improved?', icon: 'shield' },
  { label: 'How can organizations improve AI governance?', icon: 'file-text' },
  { label: 'Relationship between security and performance?', icon: 'zap' },
];

/** Analyst quick actions for a page: data-driven first, then the page's static set, max 6. */
export const quickActionsFor = (pageContext, pageId) => {
  const dataDriven = getDataDrivenActions(pageContext, pageId);
  const staticActions = getQuickActionsForPage(pageId);
  return dataDriven.length > 0
    ? [staticActions[0], ...dataDriven, ...staticActions.slice(1)].filter(Boolean).slice(0, 6)
    : staticActions;
};

/** "Page Summary" is a label; the question the model gets is the sentence. */
export const questionForAction = (label) => (label === 'Page Summary' ? 'Summarize this page' : label);
